import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import SeoHead from "@/components/SeoHead";
import Header from "@/components/Header";
import PageBreadcrumb from "@/components/PageBreadcrumb";
import Footer from "@/components/Footer";

import NewsCard from "@/components/NewsCard";
import NewsFilters from "@/components/NewsFilters";
import Section from "@/components/Section";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { fetchPublishedPosts, postRowToArticle } from "@/lib/posts";
import { useLanguage } from "@/i18n/useLanguage";
import { trackEvent } from "@/lib/analytics";

const ALL = "All";

const News = () => {
  const { t } = useTranslation();
  const { localize } = useLanguage();

  const { data: rows, isLoading, isError } = useQuery({
    queryKey: ["posts", "list"],
    queryFn: fetchPublishedPosts,
    staleTime: 5 * 60 * 1000,
  });

  const sortedArticles = useMemo(
    () =>
      (rows ?? [])
        .map((r) => postRowToArticle(r, localize(`/news/${r.slug}`)))
        .sort((a, b) => b.date.getTime() - a.date.getTime()),
    [rows, localize]
  );

  const categories = useMemo(
    () => Array.from(new Set(sortedArticles.map((a) => a.category))).sort(),
    [sortedArticles]
  );

  const [searchParams, setSearchParams] = useSearchParams();
  const initialCategory = searchParams.get("category") || ALL;
  const initialQuery = searchParams.get("q") || "";

  const [selectedCategory, setSelectedCategory] = useState<string>(initialCategory);
  const [query, setQuery] = useState<string>(initialQuery);

  useEffect(() => {
    const next = new URLSearchParams();
    if (selectedCategory && selectedCategory !== ALL) next.set("category", selectedCategory);
    if (query.trim()) next.set("q", query.trim());
    setSearchParams(next, { replace: true });
  }, [selectedCategory, query, setSearchParams]);

  const analyticsTimer = useRef<number | null>(null);
  useEffect(() => {
    if (analyticsTimer.current) window.clearTimeout(analyticsTimer.current);
    analyticsTimer.current = window.setTimeout(() => {
      trackEvent("news_filter_change", {
        category: selectedCategory,
        query_length: query.trim().length,
      });
    }, 300);
    return () => {
      if (analyticsTimer.current) window.clearTimeout(analyticsTimer.current);
    };
  }, [selectedCategory, query]);

  const filteredArticles = useMemo(() => {
    const q = query.trim().toLowerCase();
    return sortedArticles.filter((article) => {
      const matchesCategory = selectedCategory === ALL || article.category === selectedCategory;
      if (!matchesCategory) return false;
      if (!q) return true;
      return (
        article.title.toLowerCase().includes(q) ||
        article.excerpt.toLowerCase().includes(q)
      );
    });
  }, [sortedArticles, selectedCategory, query]);

  const handleClear = () => {
    setSelectedCategory(ALL);
    setQuery("");
  };

  const newsJsonLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "News & Insights",
    description:
      "Latest news and insights from AI-labbet on AI trends, regulations, best practices, and industry developments.",
    url: "https://ailabbet.com/news",
    isPartOf: {
      "@type": "WebSite",
      name: "AI-labbet",
      url: "https://ailabbet.com/",
    },
    hasPart: sortedArticles.slice(0, 20).map((article) => ({
      "@type": "Article",
      headline: article.title,
      description: article.excerpt,
      datePublished: article.date.toISOString(),
      articleSection: article.category,
    })),
  };

  return (
    <>
      <SeoHead
        title={{
          en: "News & Insights | AI-labbet",
          sv: "Nyheter & insikter | AI-labbet",
        }}
        description={{
          en: "Latest news and insights from AI-labbet on AI trends, regulations, best practices, and industry developments.",
          sv: "Senaste nyheter och insikter från AI-labbet om AI-trender, regelverk, best practice och branschutveckling.",
        }}
        path="/news"
        jsonLd={newsJsonLd}
      />

      <div className="min-h-screen bg-background flex flex-col">
        <Header />
        <PageBreadcrumb currentPage="news" />
        <main className="flex-1">
          <Section
            title={t("newsPage.title")}
            titleAs="h1"
            containerSize="wide"
            grid
          >
            <NewsFilters
              categories={categories}
              selectedCategory={selectedCategory}
              onCategoryChange={setSelectedCategory}
              query={query}
              onQueryChange={setQuery}
              resultCount={filteredArticles.length}
              totalCount={sortedArticles.length}
              onClear={handleClear}
            />

            {isLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {Array.from({ length: 6 }).map((_, i) => (
                  <Skeleton key={i} className="h-80 w-full rounded-lg" />
                ))}
              </div>
            ) : isError ? (
              <div className="text-center py-16 px-6 rounded-lg border border-border bg-card/50">
                <p className="text-lg font-medium mb-2">{t("newsPage.errorHeading", "Couldn't load news")}</p>
                <p className="text-muted-foreground">{t("newsPage.errorBody", "Please refresh the page and try again.")}</p>
              </div>
            ) : filteredArticles.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredArticles.map((article) => (
                  <NewsCard key={article.id} article={article} />
                ))}
              </div>
            ) : (
              <div className="text-center py-16 px-6 rounded-lg border border-border bg-card/50">
                <p className="text-lg font-medium mb-2">{t("newsPage.empty.heading")}</p>
                <p className="text-muted-foreground mb-6">{t("newsPage.empty.body")}</p>
                <Button variant="primary" size="sm" onClick={handleClear}>
                  {t("newsPage.empty.clear")}
                </Button>
              </div>
            )}
          </Section>
        </main>
        <Footer />
      </div>
    </>
  );
};

export default News;
